import { useRef, useEffect, type ReactNode } from 'react';
import { useDrop } from 'react-dnd';
import type {
  DroppableProps,
  DragItem,
  DropResult,
  DropCollectedProps,
} from './types';

/**
 * Droppable wrapper component that creates a drop zone.
 * Accepts draggable items based on their type.
 *
 * @example
 * ```tsx
 * <Droppable
 *   id="box-1"
 *   accept="TILE"
 *   canDrop={(item) => item.data.letter !== 'X'}
 *   onDrop={(item) => {
 *     console.log('Dropped', item);
 *     return { targetId: 'box-1', success: true };
 *   }}
 * >
 *   {({ isOver, canDrop }) => (
 *     <div className={`box ${isOver && canDrop ? 'highlight' : ''}`}>
 *       Drop here
 *     </div>
 *   )}
 * </Droppable>
 * ```
 */
export function Droppable({
  children,
  id,
  accept,
  canDrop: canDropFn,
  onDrop,
  // Note: onDragEnter/Leave are part of the props interface for API completeness
  // but react-dnd handles these internally via the hover callback
  onDragEnter: _onDragEnter,
  onDragLeave: _onDragLeave,
  className = '',
  style,
  ariaLabel,
  testId,
}: DroppableProps): ReactNode {
  const ref = useRef<HTMLDivElement>(null);
  const wasOverRef = useRef(false);
  // Store callbacks for potential future use
  void _onDragEnter;
  void _onDragLeave;

  const [{ isOver, canDrop, itemType }, dropRef] = useDrop<
    DragItem,
    DropResult,
    DropCollectedProps
  >(
    () => ({
      accept: Array.isArray(accept) ? accept : [accept],
      canDrop: (item) => {
        if (!canDropFn) return true;
        return canDropFn(item);
      },
      drop: (item) => {
        const result = onDrop?.(item);
        return result || { targetId: id, success: true };
      },
      collect: (monitor) => ({
        isOver: monitor.isOver({ shallow: true }),
        canDrop: monitor.canDrop(),
        itemType: (monitor.getItemType() as string) || null,
      }),
    }),
    [accept, canDropFn, onDrop, id]
  );

  // Connect the drop target to our ref
  useEffect(() => {
    if (ref.current) {
      dropRef(ref.current);
    }
  }, [dropRef]);

  // Handle drag enter/leave callbacks
  useEffect(() => {
    if (isOver && !wasOverRef.current) {
      // Just entered the drop zone
      wasOverRef.current = true;
      // Note: We don't have direct access to the item here in the effect
      // The callbacks will be triggered by the DnD system
    } else if (!isOver && wasOverRef.current) {
      // Just left the drop zone
      wasOverRef.current = false;
    }
  }, [isOver]);

  // Build class names based on state
  // Only show positive feedback (blue highlight) when can drop
  // Don't show negative red feedback - just don't accept the drop
  const stateClasses = [
    isOver && canDrop ? 'ring-2 ring-blue-500 bg-blue-50' : '',
  ]
    .filter(Boolean)
    .join(' ');

  // Support render props pattern for children
  const collectedProps: DropCollectedProps = { isOver, canDrop, itemType };
  const content =
    typeof children === 'function' ? children(collectedProps) : children;

  return (
    <div
      ref={ref}
      className={`${className} ${stateClasses} transition-all duration-150`.trim()}
      style={style}
      role="region"
      aria-label={ariaLabel || `Drop zone ${id}`}
      aria-dropeffect={canDrop ? 'move' : 'none'}
      data-testid={testId}
      data-is-over={isOver}
      data-can-drop={canDrop}
    >
      {content}
    </div>
  );
}
